
import { Helmet } from 'react-helmet';
import React, { useState, useEffect } from 'react';
import { useGoVehisService } from '../utils/axios';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  Box,
  Link,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  card: {
    marginTop: theme.spacing(2)
  },
  section: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 'bold'
  },
  templateLink: {
    marginBottom: theme.spacing(2)
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3)
  },
  button: {
    marginLeft: theme.spacing(1)
  },
  completedStep: {
    opacity: 0.6,
    pointerEvents: 'none'
  },
  validationSection: {
    backgroundColor: theme.palette.grey[50]
  },
  successAlert: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.success.main}`
  },
  errorAlert: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.error.main}`
  },
  infoAlert: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.info.main}`
  },
  offersTable: {
    '& .MuiTableHead-root': {
      backgroundColor: theme.palette.grey[100]
    },
    '& .MuiTableCell-head': {
      fontWeight: 'bold',
      fontSize: '0.875rem'
    },
    '& .MuiTableRow-root:nth-child(even)': {
      backgroundColor: theme.palette.grey[50]
    },
    '& .MuiTableCell-root': {
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1, 2)
    }
  },
  tableContainer: {
    maxHeight: 600,
    overflowY: 'auto'
  }
}));

const initialImportData = {
  file: null,
  contactInfo: {
    name: '',
    nip: '',
    phone: '',
    email: ''
  },
  importResponse: null,
  offers: []
};

export default function ImportOffersFromExcelPage() {
  const classes = useStyles();
  const goVehisService = useGoVehisService();
  const [importData, setImportData] = useState(initialImportData);
  const [currentStep, setCurrentStep] = useState(1); // 1, 2, 3
  const [isContactInfoComplete, setIsContactInfoComplete] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isLoadingOffers, setIsLoadingOffers] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState('idle'); // 'idle', 'success', 'error'
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);

  useEffect(() => {
    if (importData.importResponse?.uuid) {
      loadOffers(importData.importResponse.uuid);
    }
  }, [importData.importResponse, confirmStatus]);

  const loadOffers = async (importId) => {
    setIsLoadingOffers(true);
    try {
      const response = await goVehisService.get(`/offers/xlsx/${importId}/offers`);
      setImportData(prev => ({
        ...prev,
        offers: response.data
      }));
    } catch (error) {
      console.error('Failed to load offers:', error);
    } finally {
      setIsLoadingOffers(false);
    }
  };

  const translateStatus = (status) => {
    const map = {
      'CORRECT': 'Poprawny',
      'IMPORTED': 'Zaimportowany',
      'INCORRECT': 'Niepoprawny'
    };
    return map[status] || status;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'CORRECT': return 'default';
      case 'IMPORTED': return 'primary';
      case 'INCORRECT': return 'secondary';
      default: return 'default';
    }
  };

  const formatPrice = (price) => {
    if (!price) return '-';
    const num = parseFloat(price.toString().replace(/[^\d.-]/g, ''));
    return isNaN(num)
      ? price
      : new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(num);
  };

  const formatMileage = (mileage) => {
    if (!mileage) return '-';
    return new Intl.NumberFormat('pl-PL').format(mileage) + ' km';
  };

  const handleContactInfoChange = (field, value) => {
    const updated = { ...importData.contactInfo, [field]: value };
    setImportData(prev => ({ ...prev, contactInfo: updated }));

    const { name, nip, phone, email } = updated;
    setIsContactInfoComplete(name.trim() && nip.trim() && phone.trim() && email.trim());
  };

  const handleNextToFileInput = () => {
    if (isContactInfoComplete) {
      setCurrentStep(2);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0] || null;
    setImportData(prev => ({ ...prev, file }));
  };

  const handleImportSubmit = async () => {
    if (!importData.file) return;
    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importData.file);
      Object.entries(importData.contactInfo).forEach(([k, v]) => formData.append(k, v));
      const response = await goVehisService.post('/offers/xlsx', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportData(prev => ({ ...prev, importResponse: response.data }));
      setCurrentStep(3);
    } catch (error) {
      console.error('Import failed:', error);
      if (error.response?.data) {
        setImportData(prev => ({ ...prev, importResponse: error.response.data }));
        setCurrentStep(3);
      }
    } finally {
      setIsImporting(false);
    }
  };

  const handleReimportFile = () => {
    // Reset file and go back to step 2, keeping contact data
    setImportData(prev => ({
      ...prev,
      file: null,
      importResponse: null,
      offers: []
    }));
    setFileInputKey(prev => prev + 1);
    setCurrentStep(2);
    setConfirmStatus('idle');
    setConfirmMessage(null);
  };

  const handleConfirmImport = async () => {
    if (!importData.importResponse?.uuid) return;
    setIsConfirming(true);
    setConfirmStatus('idle');
    setConfirmMessage(null);
    try {
      await goVehisService.post(`/offers/xlsx/${importData.importResponse.uuid}/confirm`);
      setConfirmStatus('success');
      setConfirmMessage('Import został zatwierdzony.');
    } catch (error) {
      console.error('Confirm failed:', error);
      setConfirmStatus('error');
      setConfirmMessage(error?.response?.data?.message || 'Nie udało się zatwierdzić importu.');
    } finally {
      setIsConfirming(false);
    }
  };

  const handleRejectImport = async () => {
    if (!importData.importResponse?.uuid) return;
    setIsRejecting(true);
    try {
      await goVehisService.delete(`/offers/xlsx/${importData.importResponse.uuid}`);
      // Reset to step 1 with contact data preserved
      setImportData(prev => ({
        ...prev,
        file: null,
        importResponse: null,
        offers: []
      }));
      setFileInputKey(prev => prev + 1);
      setCurrentStep(1);
      setConfirmStatus('idle');
      setConfirmMessage(null);
    } catch (error) {
      console.error('Reject failed:', error);
      setConfirmStatus('error');
      setConfirmMessage(error?.response?.data?.message || 'Nie udało się odrzucić importu.');
    } finally {
      setIsRejecting(false);
    }
  };

  const handleStartNewImport = () => {
    // Keep contact data but reset everything else
    const preservedContactInfo = { ...importData.contactInfo };
    setImportData({
      ...initialImportData,
      contactInfo: preservedContactInfo
    });
    setFileInputKey(prev => prev + 1);
    setCurrentStep(1);
    setIsContactInfoComplete(
      preservedContactInfo.name.trim() &&
      preservedContactInfo.nip.trim() &&
      preservedContactInfo.phone.trim() &&
      preservedContactInfo.email.trim()
    );
    setConfirmStatus('idle');
    setConfirmMessage(null);
  };

  return (
    <div>
      <Helmet>
        <title>Import ofert z pliku Excel | GO-auto - Panel administracyjny</title>
      </Helmet>

      <Box style={{ minHeight: '100%' }}>
        <Container maxWidth={false} className={classes.root}>
          <Typography variant="h4" gutterBottom>
            Import ofert z pliku Excel
          </Typography>

          <Card className={classes.card}>
            <CardContent>

              {/* KROK 1: Informacja o szablonie + Dane kontaktowe */}
              <Box className={`${classes.section} ${currentStep > 1 ? classes.completedStep : ''}`}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Krok 1: Informacje o imporcie i dane kontaktowe
                </Typography>

                <Box className={classes.infoAlert}>
                  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                    Aby zaimportować oferty, użyj naszego szablonu Excel
                  </Typography>
                </Box>

                <Box className={classes.templateLink}>
                  <Typography variant="body1">
                    Pobierz szablon:{' '}
                    <Link href="/static/import_offers.xlsx" download>
                      import_offers.xlsx
                    </Link>
                  </Typography>
                </Box>

                <Divider style={{ margin: '24px 0' }} />

                <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 'bold' }}>
                  Dane kontaktowe
                </Typography>

                <Grid container spacing={3}>
                  {['name', 'nip', 'phone', 'email'].map((field, idx) => (
                    <Grid item xs={12} sm={6} md={3} key={idx}>
                      <TextField
                        required
                        fullWidth
                        label={{ name: 'Nazwa', nip: 'NIP', phone: 'Telefon', email: 'Email' }[field]}
                        type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'}
                        value={importData.contactInfo[field]}
                        onChange={(e) => handleContactInfoChange(field, e.target.value)}
                        disabled={currentStep > 1}
                      />
                    </Grid>
                  ))}
                </Grid>

                {isContactInfoComplete && (
                  <Typography variant="body2" color="primary" style={{ marginTop: 16 }}>
                    ✓ Dane kontaktowe są kompletne
                  </Typography>
                )}

                {currentStep === 1 && (
                  <div className={classes.buttons}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleNextToFileInput}
                      className={classes.button}
                      disabled={!isContactInfoComplete}
                    >
                      Dalej
                    </Button>
                  </div>
                )}
              </Box>

              {/* KROK 2: Wybór pliku */}
              {currentStep >= 2 && (
                <Box className={`${classes.section} ${currentStep > 2 ? classes.completedStep : ''}`}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Krok 2: Wybór pliku
                  </Typography>

                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12}>
                      <TextField
                        key={fileInputKey}
                        type="file"
                        inputProps={{ accept: '.xlsx,.xls' }}
                        onChange={handleFileChange}
                        fullWidth
                        variant="outlined"
                        label="Wybierz plik Excel"
                        InputLabelProps={{ shrink: true }}
                        disabled={isImporting || currentStep > 2}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      {importData.file ? (
                        <Typography variant="body2" color="primary">
                          ✓ Plik został wybrany: {importData.file?.name}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Wybierz plik Excel z ofertami do zaimportowania
                        </Typography>
                      )}
                    </Grid>
                  </Grid>

                  {currentStep === 2 && (
                    <div className={classes.buttons}>
                      <Button
                        variant="outlined"
                        onClick={() => setCurrentStep(1)}
                        className={classes.button}
                        disabled={isImporting}
                      >
                        Wstecz
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleImportSubmit}
                        className={classes.button}
                        disabled={!importData.file || isImporting}
                      >
                        {isImporting ? 'Importowanie...' : 'Importuj'}
                      </Button>
                    </div>
                  )}
                </Box>
              )}

              {/* KROK 3: Raport i potwierdzenie */}
              {currentStep >= 3 && (
                <Box className={`${classes.section} ${classes.validationSection}`}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Krok 3: Raport i potwierdzenie importu
                  </Typography>

                  {importData.importResponse ? (
                    <Box>
                      {importData.importResponse.uuid ? (
                        <Box>
                          <Box className={classes.successAlert}>
                            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                              ✓ Plik wczytany pomyślnie
                            </Typography>
                          </Box>

                          {confirmStatus === 'success' && (
                            <Box className={classes.successAlert}>
                              <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                                {confirmMessage || 'Import został zatwierdzony.'}
                              </Typography>
                            </Box>
                          )}
                          {confirmStatus === 'error' && (
                            <Box className={classes.errorAlert}>
                              <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                                {confirmMessage || 'Wystąpił błąd podczas zatwierdzania importu.'}
                              </Typography>
                            </Box>
                          )}

                          <Typography variant="body2" gutterBottom>
                            ID importu: {importData.importResponse.uuid}
                          </Typography>

                          {importData.importResponse.message && (
                            <Typography variant="body2" gutterBottom>
                              Wiadomość: {importData.importResponse.message}
                            </Typography>
                          )}

                          {isLoadingOffers ? (
                            <Typography variant="body2">Ładowanie ofert...</Typography>
                          ) : importData.offers.length > 0 ? (
                            <Box mt={3}>
                              <Typography variant="h6" gutterBottom>
                                Zaimportowane oferty ({importData.offers.length})
                              </Typography>
                              <Paper className={classes.tableContainer}>
                                <Table size="small" className={classes.offersTable} stickyHeader>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Marka</TableCell>
                                      <TableCell>Model</TableCell>
                                      <TableCell>Nadwozie</TableCell>
                                      <TableCell>Wersja</TableCell>
                                      <TableCell align="center">Rok</TableCell>
                                      <TableCell align="right">Cena</TableCell>
                                      <TableCell align="right">Przebieg</TableCell>
                                      <TableCell>Paliwo</TableCell>
                                      <TableCell>Miasto</TableCell>
                                      <TableCell align="center">Status</TableCell>
                                      <TableCell>Komunikat</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {importData.offers.map((offer, index) => (
                                      <TableRow key={index} hover>
                                        <TableCell>{offer.brand || '-'}</TableCell>
                                        <TableCell>{offer.model || '-'}</TableCell>
                                        <TableCell>{offer.bodyType || '-'}</TableCell>
                                        <TableCell>{offer.version || '-'}</TableCell>
                                        <TableCell align="center">{offer.productionYear || '-'}</TableCell>
                                        <TableCell align="right">{formatPrice(offer.price)}</TableCell>
                                        <TableCell align="right">{formatMileage(offer.mileage)}</TableCell>
                                        <TableCell>{offer.fuelType || '-'}</TableCell>
                                        <TableCell>{offer.city || '-'}</TableCell>
                                        <TableCell align="center">
                                          <Chip
                                            label={translateStatus(offer.status)}
                                            color={getStatusColor(offer.status)}
                                            size="small"
                                            variant="outlined"
                                          />
                                          {offer.vehicleId &&
                                            <Typography variant="caption" display="block">
                                              Id: {offer.vehicleId}
                                            </Typography>
                                          }
                                        </TableCell>
                                        <TableCell title={offer.message}>
                                          {offer.message || '-'}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Paper>
                            </Box>
                          ) : null}
                        </Box>
                      ) : (
                        <Box className={classes.errorAlert}>
                          <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                            ✗ Przesłany plik jest niepoprawny: {importData.importResponse.message}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Wyniki importu zostaną wyświetlone tutaj.
                    </Typography>
                  )}

                  <Divider style={{ margin: '24px 0' }} />

                  <div className={classes.buttons}>
                    {confirmStatus === 'success' ? (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleStartNewImport}
                        className={classes.button}
                      >
                        Rozpocznij nowy import
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="outlined"
                          onClick={handleReimportFile}
                          className={classes.button}
                          disabled={isConfirming || isRejecting || confirmStatus === 'success'}
                        >
                          Wprowadź nowy plik plik
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleConfirmImport}
                          className={classes.button}
                          disabled={isConfirming || isRejecting || !importData.importResponse?.uuid}
                        >
                          {isConfirming ? 'Zatwierdzanie...' : 'Zatwierdź'}
                        </Button>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={handleRejectImport}
                          className={classes.button}
                          disabled={isConfirming || isRejecting}
                        >
                          {isRejecting ? 'Odrzucanie...' : 'Odrzuć'}
                        </Button>
                      </>
                    )}
                  </div>
                </Box>
              )}

            </CardContent>
          </Card>
        </Container>
      </Box>
    </div>
  );
}
